/**
 * Fiscal Printer - item name preparation, VAT mapping, deposit detection
 */
import { VAT_RATE_MAP, PAYMENT_TYPE_MAP } from './constants';
import { rozlozRabatPoStawkach } from '../../utils/discountEngine';

/**
 * Map a numeric VAT rate to a Novitus letter.
 * -1 = ZW (zwolniony), -2 = NP (kaucje)
 */
export function mapVatRate(rate) {
  const rateNum = parseFloat(rate);
  if (rateNum === 23)  return 'A';
  if (rateNum === 8)   return 'B';
  if (rateNum === 5)   return 'C';
  if (rateNum === 0)   return 'D';
  if (rateNum === -1)  return 'Z';
  if (rateNum === -2)  return 'G';
  return 'A';
}

/**
 * Return true when a receipt line item is a deposit (kaucja).
 */
export function isDepositItem(item) {
  return item.is_deposit === true || parseFloat(item.vat_rate) === -2 || item.typ_produktu === 'kaucja';
}

/**
 * Intelligently truncate a product name for the fiscal printer display.
 * Preserves the start and end of the name where variant info typically lives.
 */
export function smartTruncateName(name, maxLength = 40) {
  if (!name) return 'Produkt';
  name = name.replace(/[<>&"']/g, '');
  if (name.length <= maxLength) return name;

  const words = name.split(' ');
  const lastWord = words[words.length - 1];
  let suffix = lastWord;
  if (lastWord.length < 6 && words.length > 2) {
    suffix = words[words.length - 2] + ' ' + lastWord;
  }

  const prefixLength = maxLength - suffix.length - 3;
  if (prefixLength < 10) {
    return name.substring(0, maxLength - 3) + '...';
  }

  const prefix = name.substring(0, prefixLength).trim();
  return prefix + '...' + suffix;
}

/**
 * Generate a stable 3-character prefix unique per (product_id, VAT rate).
 *
 * UWAGA: poprzednia wersja prefiksu bazowala na hashu NAZWY produktu.
 * Powodowalo to kolizje fiskalne: gdy 2 rozne produkty mialy podobny poczatek
 * i koncowke nazwy, smartTruncateName produkowal identyczna obcieta nazwe
 * (np. "ALLNUTRITION FITKING DELI...RASPBERRY" dla PROTEIN WAFER 23%
 * oraz SYRUP/FLAVOUR DROPS 8%). Drukarka Novitus traktowala je jako ten
 * sam produkt i rzucala blad PTU przy zmianie stawki VAT.
 *
 * Teraz prefix bazuje na (product_id, VAT) - kazdy produkt z DB ma unikalne ID,
 * wiec drukarka zawsze widzi rozne produkty niezaleznie od podobienstwa nazwy.
 *
 * Format: 3 znaki (litera + 2 znaki base36 z ID) = ~46 656 unikalnych wartosci.
 */
export function generateProductPrefix(productId, vatRate) {
  const id = parseInt(productId, 10);
  if (!id || id <= 0) {
    // Fallback do hash nazwy gdy brak ID (np. pozycje recznie wpisane)
    return null;
  }
  const vat = parseInt(vatRate, 10) || 0;
  // Litera A-Z (24 dostepnych - bez I,O,Q dla czytelnosci) wg VAT, zeby przy
  // tym samym ID rozne VAT-y dawaly INNY prefix
  const chars = 'ABCDEFGHJKLMNPRSTUVWXYZ';  // 23 znaki
  const letterIdx = ((id * 7) + vat) % chars.length;
  // 2 znaki base36 z ID - rozroznia 36*36 = 1296 produktow w obrebie litery
  const idBase36 = (id % 1296).toString(36).toUpperCase().padStart(2, '0');
  return chars[letterIdx] + idBase36;
}

/**
 * Generate a stable 2-character prefix from product name + VAT rate.
 * UWAGA: legacy - uzywane tylko gdy brak product_id (fallback).
 */
function generateNameHashPrefix(name, vatRate) {
  const input = (name || '') + '_' + (vatRate || 0);
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) - hash) + input.charCodeAt(i);
    hash = hash & hash;
  }
  hash = Math.abs(hash);
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const digits = '23456789';
  return chars[hash % chars.length] + digits[(hash >> 8) % digits.length];
}

/**
 * Process an array of receipt items, assigning unique fiscal display names.
 * Each item gets a stable prefix + truncated name; duplicates get a numeric suffix.
 *
 * Prefix bazuje na (product_id, VAT) - gwarantuje unikalnosc w pamieci drukarki
 * niezaleznie od dlugosci/podobienstwa nazwy.
 */
export function prepareItemNames(items, maxLength = 40) {
  const processedItems = items.map((item, index) => {
    const productId = item.id ?? item.produkt_id ?? item.product_id;
    let prefix = generateProductPrefix(productId, item.vat_rate);
    if (!prefix) {
      // Fallback - brak ID, uzyj hash nazwy (2-znakowy, legacy)
      prefix = generateNameHashPrefix(item.name, item.vat_rate);
    }
    const truncatedName = smartTruncateName(item.name, maxLength - prefix.length - 1);
    return { ...item, fiscalName: prefix + ' ' + truncatedName, originalIndex: index };
  });

  const nameCount = {};
  const nameOccurrence = {};

  processedItems.forEach(item => {
    nameCount[item.fiscalName] = (nameCount[item.fiscalName] || 0) + 1;
  });

  processedItems.forEach(item => {
    if (nameCount[item.fiscalName] > 1) {
      nameOccurrence[item.fiscalName] = (nameOccurrence[item.fiscalName] || 0) + 1;
      const num = nameOccurrence[item.fiscalName];
      const suffix = ` (${num})`;
      if (item.fiscalName.length + suffix.length <= maxLength) {
        item.fiscalName = item.fiscalName + suffix;
      } else {
        item.fiscalName = item.fiscalName.substring(0, maxLength - suffix.length) + suffix;
      }
      // duplicate name resolved
    }
  });

  return processedItems;
}

/**
 * Rozloz rabat globalny pro-rata per stawka VAT (zgodnie z drukarka fiskalna).
 *
 * Drukarka Novitus rejestruje rabat w pamieci fiskalnej per grupa stawki VAT,
 * wiec rabat per pozycja moze rozjechac sie o grosze przez zaokraglenia linia
 * po linii. Per stawka VAT = zero drift z raportem dobowym i KSeF.
 *
 * DELEGACJA DO WSPOLNEGO SILNIKA (2026-08): rozdzial liczy
 * utils/discountEngine.rozlozRabatPoStawkach - lustro backend/utils/vat.py
 * (te same grupy, ta sama kolejnosc reszt, ta sama regula grosza, arytmetyka
 * w groszach na int). Dzieki temu rozdzial rabatu na paragonie jest CO DO
 * GROSZA identyczny z rozdzialem zapisanym przez serwer. Dawna lokalna kopia
 * liczyla na floatach bez reguly grosza i mogla rozjechac sie z serwerem.
 *
 * @param {Array} items - pozycje koszyka z polami: quantity, price, vat_rate
 *                        lub stawka_vat, is_deposit, wykluczony_z_rabatow
 * @param {number} totalDiscount - calkowita kwota rabatu
 * @returns {number[]} tablica rabatow w tej samej kolejnosci co items
 */
export function distributeDiscountByVat(items, totalDiscount) {
  if (!totalDiscount || totalDiscount <= 0 || !items?.length) {
    return new Array(items?.length || 0).fill(0);
  }
  // Silnik czyta wartosc linii z price*quantity (badz wartosc_brutto);
  // pozycje przychodza tu juz zmapowane (quantity, price, vat_rate).
  return rozlozRabatPoStawkach(items, totalDiscount);
}

/**
 * Map a payment type string to the XML type + display name pair.
 */
export function mapPaymentType(type) {
  const typeStr = (type || 'gotowka').toLowerCase();
  const found = PAYMENT_TYPE_MAP.find(entry => entry.keys.includes(typeStr));
  if (found) return { type: found.type, name: found.name };
  return { type: 'voucher', name: 'Inna' };
}
